import { toast } from 'react-toastify';
import { IErrorModel } from '@core/models/generic.model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import OrderService from 'services/api/order.service';

// -------------------------------------------------------------------------

interface BackorderItem {
    id: number;
    product?: {
        id: number;
        itemName?: string;
        ndcCode?: string;
        barcode?: string;
        unitPrice?: number;
    };
    quantity?: number;
    remainingQuantity?: number;
    status?: string;
    order?: {
        id: number;
    };
    createdAt?: string;
}

interface BackorderResponse {
    content: BackorderItem[];
    pageable: {
        pageNumber: number;
        pageSize: number;
        offset: number;
    };
    totalPages: number;
    totalElements: number;
    numberOfElements: number;
    isFirst: boolean;
    isLast: boolean;
    isEmpty: boolean;
}

interface IBackorderState {
    backorders: BackorderItem[];
    loading: boolean;
    error: IErrorModel | undefined;
    pagination: {
        pageNumber: number;
        pageSize: number;
        totalPages: number;
        totalElements: number;
    };
}

const initialState: IBackorderState = {
    backorders: [],
    loading: false,
    error: undefined,
    pagination: {
        pageNumber: 0,
        pageSize: 20,
        totalPages: 0,
        totalElements: 0,
    },
};

// -------------------------------------------------------------------------

export const getAllBackordersThunk = createAsyncThunk<
    BackorderResponse,
    { pageNumber?: number; pageSize?: number; status?: string },
    { rejectValue: IErrorModel }
>('/backorder/getAllBackorders', async (params, { rejectWithValue }) => {
    try {
        const response: any = await OrderService.getBackorders(params);
        return response.data.Results || response.data || { content: [], pagination: {} };
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to fetch backorders', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

// -------------------------------------------------------------------------

const backorderSlice = createSlice({
    name: 'backorder',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getAllBackordersThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getAllBackordersThunk.fulfilled, (state, action) => {
            state.loading = false;
            state.backorders = action.payload.content || [];
            if (action.payload.pageable) {
                state.pagination = {
                    pageNumber: action.payload.pageable.pageNumber || 0,
                    pageSize: action.payload.pageable.pageSize || 20,
                    totalPages: action.payload.totalPages || 0,
                    totalElements: action.payload.totalElements || 0,
                };
            }
        });
        builder.addCase(getAllBackordersThunk.rejected, (state, action) => {
            state.loading = false;
            state.backorders = [];
            state.error = action.payload;
        });
    },
});

// -------------------------------------------------------------------------

export default backorderSlice;
export const backorderReducer = backorderSlice.reducer;

